/**
 * Template Form Component
 * Form per creare nuovi template WhatsApp
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';

const TemplateForm = ({ template, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEditing = !!template;
  
  const [formData, setFormData] = useState({
    name: template?.name || '',
    language: template?.language || 'it',
    category: template?.category || 'UTILITY',
    header_type: template?.header_type || 'none',
    header_text: template?.header_text || '',
    body_text: template?.body_text || '',
    footer_text: template?.footer_text || '',
    buttons: template?.buttons || [],
    variables: template?.variables || []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVariables, setShowVariables] = useState(formData.variables.length > 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-detect variables in body
    if (name === 'body_text') {
      const matches = value.match(/\{\{(\d+)\}\}/g);
      if (matches) {
        const uniquePositions = [...new Set(matches.map(m => parseInt(m.match(/\d+/)[0])))];
        const newVariables = uniquePositions.map(pos => ({
          position: pos,
          name: formData.variables.find(v => v.position === pos)?.name || `var_${pos}`,
          example: formData.variables.find(v => v.position === pos)?.example || ''
        }));
        setFormData(prev => ({ ...prev, variables: newVariables }));
        setShowVariables(true);
      }
    }
  };

  const handleVariableChange = (position, field, value) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.map(v => 
        v.position === position ? { ...v, [field]: value } : v
      )
    }));
  };

  const addButton = () => {
    if (formData.buttons.length >= 3) return;
    setFormData(prev => ({
      ...prev,
      buttons: [...prev.buttons, { type: 'QUICK_REPLY', text: '' }]
    }));
  };

  const updateButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) => 
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  const removeButton = (index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate
    if (!formData.name || !formData.body_text) {
      setError(t('templates.form.errors.required'));
      return;
    }
    
    if (!/^[a-z0-9_]+$/.test(formData.name)) {
      setError(t('templates.form.errors.invalidName'));
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        ...formData,
        buttons: formData.buttons.length > 0 ? formData.buttons : null,
        variables: formData.variables.length > 0 ? formData.variables : null
      };
      
      if (isEditing) {
        await api.put(`/api/v1/templates/${template.id}`, payload);
      } else {
        await api.post('/api/v1/templates', payload);
      }
      
      onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, t('templates.form.errors.saveFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? t('templates.form.editTitle') : t('templates.form.createTitle')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-banner" style={{ marginBottom: '1rem' }}>
                <span>⚠️ {error}</span>
              </div>
            )}
            
            {/* Name */}
            <div className="form-group">
              <label>{t('templates.form.name')} *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="conferma_appuntamento"
                disabled={isEditing}
              />
              <span className="hint">{t('templates.form.nameHint')}</span>
            </div>
            
            {/* Language & Category */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>{t('templates.form.language')}</label>
                <select 
                  name="language" 
                  value={formData.language} 
                  onChange={handleChange}
                >
                  <option value="it">🇮🇹 Italiano</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="es">🇪🇸 Español</option>
                  <option value="de">🇩🇪 Deutsch</option>
                  <option value="fr">🇫🇷 Français</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>{t('templates.form.category')}</label>
                <select 
                  name="category" 
                  value={formData.category} 
                  onChange={handleChange}
                >
                  <option value="UTILITY">🔧 Utility (Appuntamenti, Conferme)</option>
                  <option value="MARKETING">📢 Marketing (Promozioni)</option>
                  <option value="AUTHENTICATION">🔐 Authentication (OTP)</option>
                </select>
              </div>
            </div>
            
            {/* Header */}
            <div className="form-group">
              <label>{t('templates.form.header')}</label>
              <select 
                name="header_type" 
                value={formData.header_type} 
                onChange={handleChange}
              >
                <option value="none">Nessun header</option>
                <option value="text">📝 Testo</option>
              </select>
              
              {formData.header_type === 'text' && (
                <input
                  type="text"
                  name="header_text"
                  value={formData.header_text}
                  onChange={handleChange}
                  placeholder="Conferma Appuntamento"
                  maxLength={60}
                  style={{ marginTop: '0.5rem' }}
                />
              )}
            </div>
            
            {/* Body */}
            <div className="form-group">
              <label>{t('templates.form.body')} *</label>
              <textarea
                name="body_text"
                value={formData.body_text}
                onChange={handleChange}
                placeholder={`Ciao {{1}},\n\nConfermiamo il tuo appuntamento per il {{2}} alle {{3}}.\n\nIndirizzo: {{4}}\n\nA presto!`}
                rows={6}
                maxLength={1024}
              />
              <span className="hint">
                {t('templates.form.bodyHint')} • {formData.body_text.length}/1024
              </span>
            </div>
            
            {/* Variables (auto-detected) */}
            {showVariables && formData.variables.length > 0 && (
              <div className="form-group">
                <label>{t('templates.form.variables')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {formData.variables.map((variable) => (
                    <div key={variable.position} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 1fr', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: '#666' }}>{`{{${variable.position}}}`}</span>
                      <input
                        type="text"
                        value={variable.name}
                        onChange={(e) => handleVariableChange(variable.position, 'name', e.target.value)}
                        placeholder="Nome variabile"
                      />
                      <input
                        type="text"
                        value={variable.example}
                        onChange={(e) => handleVariableChange(variable.position, 'example', e.target.value)}
                        placeholder="Esempio: Mario"
                      />
                    </div>
                  ))}
                </div>
                <span className="hint">{t('templates.form.variablesHint')}</span>
              </div>
            )}
            
            {/* Footer */}
            <div className="form-group">
              <label>{t('templates.form.footer')}</label>
              <input
                type="text"
                name="footer_text"
                value={formData.footer_text}
                onChange={handleChange}
                placeholder="Rispondi per modificare"
                maxLength={60}
              />
            </div>
            
            {/* Buttons */}
            <div className="form-group">
              <label>{t('templates.form.buttons')}</label>
              
              {formData.buttons.map((button, index) => (
                <div key={index} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 40px', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <select 
                    value={button.type} 
                    onChange={(e) => updateButton(index, 'type', e.target.value)}
                  >
                    <option value="QUICK_REPLY">Quick Reply</option>
                    <option value="PHONE_NUMBER">Chiama</option>
                    <option value="URL">URL</option>
                  </select>
                  <input
                    type="text"
                    value={button.text}
                    onChange={(e) => updateButton(index, 'text', e.target.value)}
                    placeholder="Testo pulsante"
                    maxLength={25}
                  />
                  <button 
                    type="button"
                    onClick={() => removeButton(index)}
                    style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              {formData.buttons.length < 3 && (
                <button 
                  type="button" 
                  onClick={addButton}
                  style={{ padding: '0.5rem 1rem', background: '#f3f4f6', border: '1px dashed #ccc', borderRadius: '6px', cursor: 'pointer' }}
                >
                  + {t('templates.form.addButton')}
                </button>
              )}
              <span className="hint">{t('templates.form.buttonsHint')}</span>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose}
              className="btn-sync"
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-create"
              disabled={loading}
            >
              {loading ? '...' : (isEditing ? t('common.save') : t('templates.form.submit'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateForm;
